import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { useOrgSettings } from './hooks/useOrgSettings';
import Header from './components/layout/Header';
import Login from './components/auth/LoginPage';
import BusinessRegistration from './components/organization/BusinessRegistration';
import Dashboard from './components/dashboard/Dashboard';
import ForgotPassword from './components/auth/ForgotPassword';
import TimeEntry from './components/time/TimeEntry';
import PayrollPage from './components/payroll/PayrollPage';
import TeamDirectory from './components/employees/TeamDirectory';
import EmployeeProfile from './components/employees/EmployeeProfile';
import AddEmployee from './components/employees/AddEmployee';
import EditEmployee from './components/employees/EditEmployee';
import ImportEmployees from './components/employees/ImportEmployees';
import SetupManagers from './components/organization/SetupManagers';
import SetupDepartments from './components/organization/SetupDepartments';
import ManagerDashboard from './components/dashboard/ManagerDashboard';
import SchedulePage from './components/schedule/SchedulePage';
import SetPassword from './components/auth/SetPassword';
import SettingsPage from './components/organization/SettingsPage';
import SubscriptionPage from './components/billing/SubscriptionPage';
import PricingPage from './components/billing/PricingPage';
import HomePage from './components/layout/HomePage';
import AboutPage from './components/layout/AboutPage';
import PrivacyPolicyPage from './components/layout/PrivacyPolicyPage';
import TermsPage from './components/layout/TermsPage';
import PrivatePage from './components/layout/PrivatePage';
import PTOPage from './components/pto/PTOPage';
import TutorialPage from './components/help/TutorialPage';
import OrgSettings from './components/organization/OrgSettings';
import EmployeeDashboard from './components/dashboard/EmployeeDashboard';
import SsoCallback from './components/auth/SsoCallback';
import ManagerPayrollPage from './components/payroll/ManagerPayrollPage';
import ManagerTimeEntryPage from './components/payroll/ManagerTimeEntryPage';
import SupportWidget from './components/dashboard/SupportWidgets';
import QuickActionsSidebar from './components/layout/QuickActionsSidebar';
import './App.css';

const PRIVATE_MODE = process.env.REACT_APP_SITE_MODE !== 'public';

function AppFooterLinks() {
  const navigate = useNavigate();
  return (
    <span>
      <button onClick={() => navigate('/about')} style={{ background: 'none', border: 'none', color: 'var(--ce-dash-text-soft)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '0 0.5rem' }}>About</button>
      ·
      <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: 'var(--ce-dash-text-soft)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '0 0.5rem' }}>Privacy Policy</button>
      ·
      <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: 'var(--ce-dash-text-soft)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '0 0.5rem' }}>Terms &amp; Conditions</button>
      ·
      <a href="mailto:contact@flurrysystems.com" style={{ color: 'var(--ce-dash-text-soft)', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none', padding: '0 0.5rem' }}>Contact</a>
    </span>
  );
}

function AppRoutes() {
  const { user, loading, isSuperAdmin, isManager } = useAuth();
  const { features, loading: featuresLoading } = useOrgSettings();

  if (loading || (user && featuresLoading)) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '1rem', color: '#6b7280' }}>Loading...</div>;

  const isAdminAssistant = () => user?.role === 'adminassistant';
  const isEmployee = () => user?.role === 'employee';
  const hasElevatedAccess = () => isSuperAdmin() || isAdminAssistant();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: user ? 'var(--ce-dash-bg)' : undefined }}>
      {user && <Header />}
      <div style={{ flex: 1 }}>
      <Routes>
        {/* ── Public routes ── */}
        <Route path="/login" element={!user ? (PRIVATE_MODE ? <PrivatePage /> : <Login />) : <Navigate to="/" />} />
        <Route path="/register" element={PRIVATE_MODE ? <PrivatePage /> : (!user ? <BusinessRegistration /> : <Navigate to="/" />)} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/sso-callback" element={<SsoCallback />} />

        {/* ── Root dashboard — role-based ── */}
        <Route
          path="/"
          element={
            !user ? <HomePage /> :
            isSuperAdmin() ? <Dashboard /> :
            isAdminAssistant() ? <Dashboard /> :
            isManager() ? <ManagerDashboard /> :
            isEmployee() ? <EmployeeDashboard /> :
            <Navigate to="/login" />
          }
        />

        {/* ── Employee dashboard ── */}
        <Route
          path="/my-dashboard"
          element={user ? <EmployeeDashboard /> : <Navigate to="/login" />}
        />

        {/* ── Settings ── */}
        <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/login" />} />
        <Route path="/settings/org" element={user && isSuperAdmin() ? <OrgSettings /> : <Navigate to="/" />} />
        <Route path="/subscription" element={user && isSuperAdmin() ? <SubscriptionPage /> : <Navigate to="/" />} />

        {/* ── Manager payroll (private — server enforces access) ── */}
        <Route path="/manager-payroll" element={user ? <ManagerPayrollPage /> : <Navigate to="/login" />} />

        {/* ── Manager time entry — superadmin/admin only ── */}
        <Route path="/manager-time-entry" element={user && (isSuperAdmin() || user.role === 'admin') ? <ManagerTimeEntryPage /> : <Navigate to="/" />} />

        {/* ── Shared routes ── */}
        <Route path="/time-cards" element={user && features.timeEntry && (isSuperAdmin() || isAdminAssistant()) ? <TimeEntry /> : <Navigate to="/" />} />
        <Route path="/team" element={user ? <TeamDirectory /> : <Navigate to="/login" />} />
        <Route path="/employee/:id" element={user ? <EmployeeProfile /> : <Navigate to="/login" />} />
        <Route
          path="/schedule"
          element={!user ? <Navigate to="/login" /> : features.scheduling ? <SchedulePage /> : <Navigate to="/" />}
        />

        {/* ── Payroll ── */}
        <Route
          path="/payroll"
          element={user && features.payroll && (isSuperAdmin() || isAdminAssistant() || isManager()) ? <PayrollPage /> : <Navigate to="/" />}
        />

        {/* ── Add employee ── */}
        <Route
          path="/employee/new"
          element={user && (isSuperAdmin() || isAdminAssistant() || isManager()) ? <AddEmployee /> : <Navigate to="/" />}
        />

        {/* ── Import employees ── */}
        <Route
          path="/employee/import"
          element={user && (isSuperAdmin() || isAdminAssistant() || isManager()) ? <ImportEmployees /> : <Navigate to="/" />}
        />

        {/* ── Edit employee ── */}
        <Route
          path="/employee/:id/edit"
          element={user && (isSuperAdmin() || isAdminAssistant() || isManager()) ? <EditEmployee /> : <Navigate to="/" />}
        />

        {/* ── Setup Managers ── */}
        <Route
          path="/setup-managers"
          element={user && hasElevatedAccess() ? <SetupManagers /> : <Navigate to="/" />}
        />

        {/* ── Setup Departments ── */}
        <Route
          path="/setup-departments"
          element={user && (hasElevatedAccess() || isManager()) ? <SetupDepartments /> : <Navigate to="/" />}
        />

        {/* ── PTO ── */}
        <Route
          path="/pto"
          element={user && features.ptoTracking && (isSuperAdmin() || isManager()) ? <PTOPage /> : <Navigate to="/" />}
        />

        {/* ── Tutorial / Help ── */}
        <Route path="/tutorial" element={user ? <TutorialPage /> : <Navigate to="/login" />} />
      </Routes>
      </div>

      {user && <QuickActionsSidebar />}

      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 999 }}>
        <SupportWidget />
      </div>

      {user && (
        <footer style={{ textAlign: 'center', padding: '1.25rem', borderTop: '1px solid var(--ce-card-border)', fontSize: '0.8rem', color: 'var(--ce-dash-text-soft)' }}>
          <AppFooterLinks />
        </footer>
      )}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}